import { SEO_TITLE_STROLLER_REVIEWS_stroller, STROLLER_SEO_GENERAL_DESC } from 'mk/autogenerated/translations/StrollerAllReviewsPhotosGalleryPage.182e58665e8946635aba9b957e110c26'
import {
    strollerAllReviewsPhotosFetchTrigger,
    strollerAllReviewsPhotosToggleThumbsTrigger,
} from 'mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.actions';
import {
    strollersAllReviewsPhotosReducer,
} from 'mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.reducers';
import strollersAllReviewsPhotosSaga
    from 'mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.sagas';
import {
    getStrollerAllReviewsPhotosPageState,
} from 'mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.selectors';
import {
    StrollerAllReviewsPhotosGallery,
} from 'mk2/apps/strollers/containers/StrollerAllReviewsPhotosGallery/StrollerAllReviewsPhotosGallery';
import { strollersReviewsUrl } from 'mk2/apps/strollers/urls';
import { Btn, BtnType } from 'mk2/components/Btn';
import { AppName } from 'mk2/containers/Application/Application';
import { cacheLast } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import { AppState } from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import {
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewSchema,
} from 'mk2/schemas';
import { getDenormalizedEntities, getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    loadingState: LoadingState;
    stroller: StrollerEntity;
    strollerReviews: StrollerUserReviewEntity[];
    strollerReviewsPhotos: StrollerUserReviewPhotoEntity[];
}

export interface DispatchProps {
    onLoad(slug: string);
    onToggleThumbnails();
}

export type RouteProps = RouteComponentProps<{
    slug: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerAllReviewsPhotosGalleryPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {

    private photosCache = cacheLast<StrollerUserReviewPhotoEntity[]>(true);
    private handleOnExit = cacheLast();

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.slug;

            const photosState = getStrollerAllReviewsPhotosPageState(state, slug);
            const stroller = getDenormalizedEntity<StrollerEntity>(state, StrollerSchema, photosState.strollerId);
            const strollerReviews = getDenormalizedEntities<StrollerUserReviewEntity>(state, StrollerUserReviewSchema, photosState.strollerReviewIds);

            const strollerReviewsPhotos = this.photosCache(
                () => strollerReviews.reduce((ret, review) => [...ret, ...review.photos], []), // collect all review photos
                strollerReviews,
            );

            return {
                strollerSlug: slug,
                loadingState: photosState.loadingState,
                stroller,
                strollerReviews,
                strollerReviewsPhotos,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerAllReviewsPhotosFetchTrigger,
            onToggleThumbnails: strollerAllReviewsPhotosToggleThumbsTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name }) : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerAllReviewsPhotosGallery {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false; /* custom back button */
    }

    public getHeaderLeft(props: Props): React.ReactElement {
        const { strollerSlug, history } = props;
        return (
            <Btn
                icon="mkicon-arrow-back@3x.png"
                onClick={this.handleOnExit(() => {
                    const gotoUrl = url(strollersReviewsUrl, { slug: strollerSlug });
                    history.replace(gotoUrl);
                }, strollerSlug)}
                data-cy="page-back-button"
                type={BtnType.BlueText}
            />
        );
    }


    public getDefaultBackPath(props: Props): string {
        const { strollerSlug } = props;
        return url(strollersReviewsUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: 'Strollers Index - Halfpage Sticky (Desktop)',
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.strollers.strollersAllReviewsPhotos', strollersAllReviewsPhotosReducer);
        store.injectSaga('containers.strollers.strollersAllReviewsPhotos', strollersAllReviewsPhotosSaga);
    }
}
